import apiClient from '../utils/apiClient';

/**
 * Employee API Services
 * Quản lý nhân viên, thông tin cá nhân và thống kê
 */

export const employeeAPI = {
  /**
   * Lấy danh sách nhân viên với phân trang và tìm kiếm
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.search - Từ khóa tìm kiếm
   * @param {number} params.page - Trang hiện tại
   * @param {number} params.limit - Số lượng mỗi trang
   * @returns {Promise<Object>} Danh sách nhân viên và metadata phân trang
   */
  getEmployees: async (params = {}) => {
    const { search = '', page = 1, limit = 10, branch_id, status, role } = params;
    return await apiClient.get({
      url: '/employees',
      params: { search, page, limit, branch_id, status, role },
    });
  },

  /**
   * Lấy thông tin chi tiết nhân viên theo ID
   * @param {string} employeeId - ID nhân viên
   * @returns {Promise<Object>} Thông tin chi tiết nhân viên
   */
  getEmployeeById: async (employeeId) => {
    return await apiClient.get({
      url: `/employees/${employeeId}`,
    });
  },

  /**
   * Lấy thống kê cá nhân của nhân viên hiện tại
   * @returns {Promise<Object>} Thống kê cá nhân
   */
  getMyStats: async () => {
    return await apiClient.get({
      url: '/employees/my-stats',
    });
  },

  /**
   * Tạo nhân viên mới (ADMIN only)
   * @param {Object} employeeData - Dữ liệu nhân viên
   * @param {string} employeeData.username - Tên đăng nhập
   * @param {string} employeeData.email - Email
   * @param {string} employeeData.password - Mật khẩu
   * @param {string} employeeData.role - Vai trò (STAFF, MANAGER, ADMIN)
   * @param {string} employeeData.branchId - ID chi nhánh
   * @returns {Promise<Object>} Thông tin nhân viên vừa tạo
   */
  createEmployee: async (employeeData) => {
    return await apiClient.post({
      url: '/employees',
      data: employeeData,
    });
  },

  /**
   * Cập nhật thông tin nhân viên
   * @param {string} employeeId - ID nhân viên
   * @param {Object} updateData - Dữ liệu cập nhật
   * @returns {Promise<Object>} Thông tin nhân viên sau khi cập nhật
   */
  updateEmployee: async (employeeId, updateData) => {
    return await apiClient.patch({
      url: `/employees/${employeeId}`,
      data: updateData,
    });
  },

  /**
   * Xóa nhân viên (soft delete)
   * @param {string} employeeId - ID nhân viên
   * @returns {Promise<Object>} Kết quả xóa
   */
  deleteEmployee: async (employeeId) => {
    return await apiClient.delete({
      url: `/employees/${employeeId}`,
    });
  },

  /**
   * Cập nhật trạng thái nhân viên
   * @param {string} employeeId - ID nhân viên
   * @param {string} status - Trạng thái mới (ACTIVE, INACTIVE)
   * @returns {Promise<Object>} Kết quả cập nhật
   */
  updateEmployeeStatus: async (employeeId, status) => {
    return await apiClient.patch({
      url: `/employees/${employeeId}`,
      data: { status },
    });
  },

  /**
   * Lấy danh sách nhân viên theo chi nhánh
   * @param {string} branchId - ID chi nhánh
   * @param {Object} params - Tham số truy vấn
   * @returns {Promise<Object>} Danh sách nhân viên của chi nhánh
   */
  getEmployeesByBranch: async (branchId, params = {}) => {
    const { search = '', page = 1, limit = 10, status } = params;
    return await apiClient.get({
      url: '/employees',
      params: { branch_id: branchId, search, page, limit, status },
    });
  },

  getEmployeeMonthlyShiftsStats: async ({ employeeId, month, branch_id }) => {
    const params = {
      ...(month ? { month } : {}),
      ...(branch_id ? { branch_id } : {}),
    };
    return await apiClient.get({
      url: `/employees/${employeeId}/stats/monthly-shifts`,
      params,
    });
  },

  /**
   * Thống kê ca làm việc trong tuần
   * @param {Object} params - Tham số truy vấn
   * @param {string} params.employeeId - ID nhân viên
   * @param {string} params.week - YYYY-MM-DD (ngày bất kỳ trong tuần)
   * @param {string} params.branch_id - ID chi nhánh (tùy chọn)
   * @returns {Promise<Object>} Thống kê ca làm việc trong tuần
   */
  getEmployeeWeeklyShiftsStats: async ({ employeeId, week, branch_id }) => {
    const params = {
      ...(week ? { week } : {}),
      ...(branch_id ? { branch_id } : {}),
    };
    return await apiClient.get({
      url: `/employees/${employeeId}/stats/weekly-shifts`,
      params,
    });
  },

  getEmployeeMonthlyPointsStats: async ({ employeeId, month, branch_id }) => {
    const params = {
      ...(month ? { month } : {}),
      ...(branch_id ? { branch_id } : {}),
    };
    return await apiClient.get({
      url: `/employees/${employeeId}/stats/monthly-points`,
      params,
    });
  },

  getEmployeePreviousMonthStats: async ({ employeeId, month, branch_id }) => {
    const params = {
      ...(month ? { month } : {}),
      ...(branch_id ? { branch_id } : {}),
    };
    return await apiClient.get({
      url: `/employees/${employeeId}/stats/previous-month`,
      params,
    });
  },

  getEmployeeCareerPoints: async ({ employeeId, branch_id }) => {
    const params = {
      ...(branch_id ? { branch_id } : {}),
    };
    return await apiClient.get({
      url: `/employees/${employeeId}/stats/career-points`,
      params,
    });
  },

  /**
   * Đánh dấu nhân viên rời đi và vô hiệu hóa toàn bộ ca ưu tiên
   * @param {string} employeeId - ID nhân viên
   * @returns {Promise<Object>} Kết quả cập nhật
   */
  markAsLeaving: async (employeeId) => {
    return await apiClient.post({
      url: `/employees/${employeeId}/mark-leaving`,
    });
  },

  broadcastTelegram: async ({
    message,
    branch_id,
    attachment_type,
    attachment_url,
    attachment_key,
  }) => {
    return await apiClient.post({
      url: '/employees/broadcast-telegram',
      data: {
        message,
        branch_id,
        attachment_type,
        attachment_url,
        attachment_key,
      },
    });
  },

  /**
   * Lịch sử các lần gửi thông báo Telegram (ADMIN only) — US-153307 / #222424
   * @param {Object} params - { branch_id, from, to, page, page_size }
   */
  getBroadcastHistory: async ({ branch_id, from, to, page, page_size } = {}) => {
    return await apiClient.get({
      url: '/employees/broadcast-telegram/history',
      params: { branch_id, from, to, page, page_size },
    });
  },
};
